<template>
  <div :class="$style.page">
    <BaseContainer :class="$style.container">
      <form @submit.prevent="submit">
        <BaseLoader
          :class="$style.box"
          :is-active="isFormSending"
        >
          <FormBox
            :heading="formStepData.heading"
            :lead="formStepData.lead"
          >
            <template v-if="formStep === 1">
              <BaseField
                is-required
                :class="$style.field"
                :error="companyNameError"
              >
                <template #label>
                  COMPANY NAME
                </template>

                <BaseInput
                  v-model="companyName"
                  :has-error="Boolean(companyNameError)"
                />
              </BaseField>

              <FormBoxDivider />

              <div>
                <h4 :class="$style.featuresHeading">
                  What features will be made available to this company?
                </h4>
                <p :class="$style.featuresHelp">
                  Description goes here to explain what this is all about.
                </p>
                <div :class="$style.featuresOptions">
                  <BaseLabeledCheckbox
                    v-for="option in $options.FEATURES_OPTIONS"
                    :key="option.value"
                    v-model="features"
                    :value="option.value"
                    is-dark
                    has-background-highlight
                  >
                    {{ option.label }}
                  </BaseLabeledCheckbox>
                </div>
              </div>

              <div :class="$style.renewalSection">
                <h4 :class="$style.renewalHeading">
                  Paid until
                </h4>
                <div :class="$style.renewalDate">
                  <BaseDateInput
                    v-model="paidUntil"
                    :class="$style.dateInput"
                    :model-config="{ timeAdjust: '23:59:59' }"
                  />
                </div>
              </div>
            </template>

            <template v-else-if="formStep === 2">
              <div :class="$style.billingSection">
                <h4 :class="$style.billingHeading">
                  Type
                </h4>
                <div :class="$style.billingOptions">
                  <div
                    v-for="option in $options.BILLING_OPTIONS"
                    :key="option.value"
                    :class="$style.billingOption"
                    @click.exact="billing = option.value"
                  >
                    <BaseLabeledRadio
                      v-model="billing"
                      :value="option.value"
                      is-dark
                    >
                      {{ option.label }}
                    </BaseLabeledRadio>
                  </div>
                </div>
              </div>
            </template>

            <template v-else-if="formStep === 3">
              <BaseField
                is-required
                :error="emailError"
                :class="$style.field"
              >
                <template #label>
                  EMAIL
                </template>

                <BaseInput
                  v-model="email"
                  type="email"
                  :has-error="Boolean(emailError)"
                />
              </BaseField>
              <BaseField
                is-required
                :error="givenNameError"
                :class="$style.field"
              >
                <template #label>
                  GIVEN NAME
                </template>

                <BaseInput
                  v-model="givenName"
                  :has-error="Boolean(givenNameError)"
                />
              </BaseField>
              <BaseField
                is-required
                :error="familyNameError"
                :class="$style.field"
              >
                <template #label>
                  FAMILY NAME
                </template>

                <BaseInput
                  v-model="familyName"
                  :has-error="Boolean(familyNameError)"
                />
              </BaseField>
              <BaseField
                is-required
                :class="$style.field"
                :error="timeZoneError"
              >
                <template #label>
                  TIME ZONE
                </template>

                <BaseSelect
                  v-model="timeZone"
                  :class="$style.multiselect"
                  :has-error="Boolean(timeZoneError)"
                >
                  <option disabled>
                    Select
                  </option>
                  <option
                    v-for="option in $options.TIME_ZONES_OPTIONS"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </BaseSelect>
              </BaseField>

              <FormBoxDivider />

              <ul :class="$style.infoSection">
                <li>
                  <span :class="$style.infoTick">
                    <TickSvg />
                  </span>
                  This user's email will be used as the billing email address for the company.
                </li>
                <li>
                  <span :class="$style.infoTick">
                    <TickSvg />
                  </span>
                  They will have access to all functionality that company has been given access to.
                </li>
              </ul>
            </template>

            <template #footer>
              <BaseButton
                variant="success"
                type="submit"
                is-block
              >
                {{ formStepData.submitText }}
              </BaseButton>
            </template>
          </FormBox>
        </BaseLoader>
      </form>
    </BaseContainer>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import { mapState } from 'vuex';
import { v4 as uuidv4 } from 'uuid';
import BaseContainer from '@/components/base/Container';
import BaseField from '@/components/base/Field';
import BaseInput from '@/components/base/Input';
import BaseDateInput from '@/components/base/DateInput';
import BaseLabeledCheckbox from '@/components/base/LabeledCheckbox';
import BaseLabeledRadio from '@/components/base/LabeledRadio';
import BaseButton from '@/components/base/Button';
import BaseSelect from '@/components/base/Select';
import BaseLoader from '@/components/base/Loader';
import FormBox, { FormBoxDivider } from '@/components/FormBox';
import TickSvg from '@/assets/images/icons/tick.svg?inline';
import { adminQueries } from '@/aws-api';
import { timeZones, companyFeatures, billingTypes } from '@/config';

export default {
  components: {
    BaseContainer,
    BaseField,
    BaseInput,
    BaseDateInput,
    BaseLabeledCheckbox,
    BaseLabeledRadio,
    BaseButton,
    BaseSelect,
    BaseLoader,
    FormBox,
    FormBoxDivider,
    TickSvg,
  },
  TIME_ZONES_OPTIONS: timeZones,
  FEATURES_OPTIONS: companyFeatures,
  BILLING_OPTIONS: billingTypes,
  FORM_STEPS_DATA: [
    {
      heading: 'Create new company',
      submitText: 'Continue',
    },
    {
      heading: 'Company billing details',
      lead: 'These are for your records as you raise invoices manually.',
      submitText: 'Continue',
    },
    {
      heading: 'Add the first user to the team',
      submitText: 'Send invite',
    },
    {
      heading: 'Invite sent!',
      lead: 'They’ll receive an email shortly with their invite details included.',
      submitText: 'Go to users',
    },
  ],
  data() {
    return {
      companyName: '',
      companyNameError: null,
      features: [],
      paidUntil: null,
      billing: this.$options.BILLING_OPTIONS[0].value,
      givenName: '',
      givenNameError: null,
      familyName: '',
      familyNameError: null,
      email: '',
      emailError: null,
      timeZone: null,
      timeZoneError: null,
      isFormSending: false,
      formStep: 1,
    };
  },
  computed: {
    ...mapState('user', ['user']),
    formStepData() {
      return this.$options.FORM_STEPS_DATA[this.formStep - 1];
    },
  },
  methods: {
    submitStepOne() {
      this.companyNameError = null;

      if (this.companyName.trim() === '') {
        this.companyNameError = 'Can\'t be empty';
        return;
      }

      this.formStep += 1;
    },
    submitStepTwo() {
      this.formStep += 1;
    },
    async submitStepThree() {
      this.emailError = null;
      this.givenNameError = null;
      this.familyNameError = null;
      this.timeZoneError = null;
      let hasError = false;

      if (this.email.trim() === '') {
        hasError = true;
        this.emailError = 'Can\'t be empty';
      }

      if (this.givenName.trim() === '') {
        hasError = true;
        this.givenNameError = 'Can\'t be empty';
      }

      if (this.familyName.trim() === '') {
        hasError = true;
        this.familyNameError = 'Can\'t be empty';
      }

      if (this.timeZone == null) {
        hasError = true;
        this.timeZoneError = 'Can\'t be empty';
      }

      if (hasError) {
        return;
      }

      this.isFormSending = true;
      try {
        const { user } = await adminQueries.createUser(this.email, this.givenName, this.familyName);

        try {
          const res = await this.$apollo.mutate({
            mutation: gql`
              mutation (
                $userId: String!,
                $timeZone: String!,
                $companyName: String!,
                $billingType: String!,
                $companyUuid: uuid!,
                $enabledFeatures: _varchar,
                $invoices: invoices_arr_rel_insert_input,
              ) {
                insert_companies_one(
                  object: {
                    name: $companyName,
                    billing_type: $billingType,
                    enabled_features: $enabledFeatures,
                    invoices: $invoices,
                    uuid: $companyUuid,
                    owner: {
                      data: {
                        id: $userId,
                        time_zone: $timeZone,
                      }
                    }
                  }
                 ) {
                  id
                  owner_id
                }
              }
            `,
            variables: {
              userId: user.Username,
              companyUuid: uuidv4(),
              timeZone: this.timeZone,
              companyName: this.companyName,
              billingType: this.billing,
              enabledFeatures: `{${this.features.join(',')}}`,
              invoices: this.paidUntil
                ? ({ data: { paid_until: this.paidUntil, created_by: this.user.username } })
                : null,
            },
          });

          await this.$apollo.mutate({
            mutation: gql`
              mutation ($userId: String!, $companyId: Int!) {
                update_users_by_pk(
                  pk_columns: { id: $userId },
                  _set: {company_id: $companyId}
                ) {
                  company_id
                }
              }
            `,
            variables: {
              userId: user.Username,
              companyId: res.data.insert_companies_one.id,
            },
          });

          this.formStep = 4;
        } catch (e) {
          console.error(e); // eslint-disable-line no-console
        }
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        this.emailError = e.response.data.message;
      }
      this.isFormSending = false;
    },
    submit() {
      switch (this.formStep) {
        case 1:
          return this.submitStepOne();
        case 2:
          return this.submitStepTwo();
        case 3:
          return this.submitStepThree();
        case 4:
          return this.$router.push({ name: 'Users' });
        default:
          return null;
      }
    },
  },
};
</script>

<style lang="scss" module>
.page {
  flex-grow: 1;
  padding-top: map-get($page-vertical-paddings, 'small');
  padding-bottom: map-get($page-vertical-paddings, 'small');
  background: #fafafa;
}

.container {
  display: flex;
  justify-content: center;
}

.box {
  width: 470px;
}

.field {

  &:not(:first-child) {
    margin-top: 20px;
  }
}

.multiselect {
  width: 100%;
}

.featuresHeading {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: $title;
}

.featuresHelp {
  margin: 0;
  margin-top: 2px;
  font-size: 13px;
  color: $text;
}

.featuresOptions {
  margin-top: 6px;

  > * {

    &:not(:first-child) {
      margin-top: 2px;
    }
  }
}

.renewalSection {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  margin-top: 20px;
  background: $background-light;
  border-radius: 5px;
}

.renewalHeading {
  margin: 0;
  font-size: 16px;
  color: $title;
}

.renewalDate {
  display: flex;
}

.dateInput {
  width: 250px;
}

.billingSection {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: $background-light;
  border-radius: 5px;
}

.billingHeading {
  margin: 0;
  font-size: 16px;
  color: $title;
}

.billingOptions {
  display: flex;
}

.billingOption {
  padding: 8px;
  background: $white;
  border: 1px solid #e6e6e6;
  border-radius: 5px;

  &:not(:first-child) {
    margin-left: 5px;
  }
}

.infoSection {
  padding: 0;
  padding-left: 20px;
  margin: 0;

  li {
    position: relative;
    display: block;
    font-size: 16px;
    line-height: 1.375;
    color: $title;

    &:not(:first-child) {
      margin-top: 10px;
    }
  }
}

.infoTick {
  position: absolute;
  top: 0;
  left: -20px;
  color: $title;

  svg {
    width: 12px;
    height: auto;
  }
}
</style>
